// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import ReactSVG from 'react-svg';
import { isMobile, CustomView, isMobileOnly } from 'react-device-detect';

import SubHeader from '../../components/SubHeader';
import SearchBar from '../../components/SearchBar';
import ResultCard from '../../components/ResultCard';
import FacetFilter from '../../components/FacetFilter';
import PaginationResults from '../../components/PaginationResults';
import { svgStyle } from '../../components/ComboFilter/style';

import { Wrapper } from './style';
import ChevronOuvre2 from '../../assets/images/chevron2_off.svg';
import { makeSearch } from '../../actions/search';

import type { Document } from '../../types/Document';
import CloseIcon from '../../assets/images/close.svg';
import Spinner from '../../assets/images/spinner-solid-full.svg';
import Question from '../../assets/images/question-solid-full-white.svg';
import { FormattedMessage } from 'react-intl';
import { formatMessage } from '../../CurrentLocale';
import history from '../../history';

import NoDocIcon from '../../assets/images/no_doc.png';

import ExportCSVButton from '../../components/ExportCSVButton/index';
import RSSFluxButton from '../../components/RSSFluxButton/index';
import SearchHelpPopup from '../../components/SearchHelpPopup';

type Props = {
  documents: Array<Document>,
  makeSearch: () => void,
  counts: Object,
  totalDocumentCount: number,
  lang: string
};

type State = {
  displaySidebar: boolean,
  screenWidth: ?number
};

class Result extends Component<Props, State> {
  searchParams = new URLSearchParams(window.location.search);
  state = {
    displaySidebar: !isMobile,
    screenWidth: null,
    scrolled: false,
    hasSearch: this.searchParams.get('searched') === '1',
    loading: true,
    popupOpen: false,
    activeHelpTab: 'search'
  };

  componentDidMount() {
    this.updateDimensions();
    this.props.makeSearch();
    window.addEventListener('resize', this.updateDimensions);
    window.addEventListener('scroll', this.handleScroll);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.results.loading && !this.props.results.loading) {
      this.setState({ loading: false });
    }
  }

  updateDimensions = () => {
    this.setState({
      screenWidth: Math.max(
        //$FlowFixMe
        document.documentElement.clientWidth,
        window.innerWidth || 0
      )
    });
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
    window.removeEventListener('scroll', this.handleScroll);
  }

  handlePopupToggle = () => {
    this.setState({ popupOpen: !this.state.popupOpen });
  };

  handleScroll = () => {
    const scrolled = window.scrollY > 150;

    if (scrolled !== this.state.scrolled) {
      this.setState({ scrolled });
    }
  };

  handleTogglSideBar = () => {
    this.setState({ displaySidebar: !this.state.displaySidebar });

    window.scrollTo(0, 0);
  };

  haveFacets = () => {
    const { counts } = this.props;

    return Object.keys(counts).length !== 0;
  };

  runSearch = () => {
    const { query } = this.state;

    this.props.setCriteria({ query });
    this.props.resetFacetState();
    this.props.makeSearch();
    this.decodeQueryUri();
  };

  goToAdvancedSearch = () => {
    history.replace({
      pathname: '/advanced',
      search: history.location.search
    });
  };

  thousandsDisplay = number => {
    const { lang } = this.props;
    return new Intl.NumberFormat((lang ? lang : 'fr') + '-FR').format(number);
  };

  render() {
    const { displaySidebar, screenWidth } = this.state;
    const { documents, totalDocumentCount } = this.props;

    if (!screenWidth) return null;

    const columns = {};

    let nbColumn;
    if (screenWidth < 700) nbColumn = 1;
    else if (screenWidth < 1024) nbColumn = 2;
    else if (screenWidth < 1367) nbColumn = displaySidebar ? 2 : 3;
    else nbColumn = displaySidebar ? 3 : 4;

    let i = 1;
    documents.forEach(document => {
      if (!columns[`column${i}`]) columns[`column${i}`] = [];
      columns[`column${i}`].push(
        <ResultCard key={document.url} {...document} />
      );

      if (i >= nbColumn) i = 1;
      else i += 1;
    });

    const searchParams = new URLSearchParams(window.location.search);

    return (
      <Wrapper>
        {searchParams.get('searched') ? (
          <React.Fragment>
            {this.state.popupOpen && (
              <SearchHelpPopup
                onToggle={this.handlePopupToggle}
                activeTab={this.state.activeHelpTab}
              />
            )}
            <SubHeader title={formatMessage({ id: 'result.search_doc' })} />
            <div
              className={`container ${
                searchParams.get('searched')
                  ? this.state.scrolled
                    ? 'container--query container--query--scrolled'
                    : 'container--query'
                  : ''
              }`}
            >
              <SearchBar />
            </div>
            <div className="root-container">
              {!this.state.loading &&
                documents.length > 0 && (
                  <div className="result-title">
                    <div className="result-title__refine" />
                    <div className="result-title__count">
                      <div className="result-title__count__text">
                        <h2>
                          {totalDocumentCount < 2 ? (
                            <React.Fragment>
                              {this.thousandsDisplay(totalDocumentCount)}
                              <FormattedMessage id="paginationResult.result_found" />
                            </React.Fragment>
                          ) : (
                            <React.Fragment>
                              {this.thousandsDisplay(totalDocumentCount)}
                              <FormattedMessage id="paginationResult.results_found" />
                            </React.Fragment>
                          )}
                        </h2>
                      </div>
                      <div className="result-title__count__buttons">
                        <ExportCSVButton data={documents} separator="," />
                        <RSSFluxButton />
                      </div>
                    </div>
                  </div>
                )}
              {!this.state.loading &&
                documents.length > 0 && (
                  <div className="result-header">
                    <div className="header">
                      <div className="header__left">
                        <div className="text">
                          <FormattedMessage id="result.select_filters" />
                        </div>
                        <div className="button--help">
                          <div className="button--help__tooltip">
                            <FormattedMessage id="advancedSearch.search_help" />
                          </div>
                          <button
                            className="advanced__header__button advanced__header__button--results"
                            type="button"
                            onClick={() =>
                              this.setState({
                                activeHelpTab: 'search',
                                popupOpen: true
                              })
                            }
                          >
                            
                            <img
                              className="advanced__header__button__img"
                              src={Question}
                              alt="Open the help search popup"
                            />
                          </button>
                        </div>
                      </div>
                      <div
                        className="icon-sidebar"
                        onClick={this.handleTogglSideBar}
                      >
                        <div
                          className="icon-sidebar--anim"
                          style={{
                            transform:
                              screenWidth < 1366
                                ? 'rotate(0deg)'
                                : displaySidebar
                                  ? 'rotate(-90deg)'
                                  : 'rotate(90deg)'
                          }}
                        >
                          <ReactSVG
                            svgStyle={{ width: 20 }}
                            src={ChevronOuvre2}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="result-pagination">
                      {documents.length !== 0 && (
                        <PaginationResults screenWidth={screenWidth} />
                      )}
                    </div>
                  </div>
                )}
              <div className="result-container">
                {!this.state.loading &&
                  documents.length > 0 && (
                    <div
                      className="facet-zone"
                      style={{
                        display: displaySidebar ? 'block' : 'none',
                        zIndex: isMobileOnly ? 101 : 'initial'
                      }}
                    >
                      <CustomView condition={screenWidth <= 1024}>
                        <div
                          className="close-filters"
                          onClick={this.handleTogglSideBar}
                        >
                          <div>
                            <FormattedMessage id="result.filters" />
                          </div>
                          <ReactSVG svgStyle={svgStyle} src={CloseIcon} />
                        </div>
                      </CustomView>
                      <FacetFilter />
                    </div>
                  )}
                <div className="result-zone">
                  {documents.length === 0 ? (
                    <React.Fragment>
                      {!this.state.loading ? (
                        <div
                          style={{
                            textAlign: 'center',
                            fontSize: 24,
                            marginLeft: 20,
                            marginRight: 20,
                            marginTop: 30
                          }}
                        >
                          <p
                            style={{
                              margin: 0
                            }}
                          >
                            {formatMessage({ id: 'result.no_document_found' })}
                            <br />
                            {formatMessage({ id: 'result.not_corresponding' })}
                          </p>
                          <button
                            className="main-container__search__link"
                            type="button"
                            onClick={this.goToAdvancedSearch}
                          >
                            {this.searchParams.get('advanced')
                              ? formatMessage({
                                  id: 'searchBar.edit_the_search'
                                })
                              : formatMessage({
                                  id: 'searchBar.new_advanced_search'
                                })}
                          </button>
                        </div>
                      ) : (
                        <div className="loading">
                          <img src={Spinner} alt="Loading" />
                        </div>
                      )}
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <div className="results">
                        {Object.values(columns).map((column, index) => (
                          //$FlowFixMe
                          <div
                            style={{ width: `calc(100% / ${nbColumn})` }}
                            key={index}
                          >
                            {column}
                          </div>
                        ))}
                      </div>
                      <div className="result-pagination">
                        <PaginationResults
                          screenWidth={screenWidth}
                          onlyPagination={isMobile}
                          paddingTop={51}
                        />
                      </div>
                    </React.Fragment>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        ) : (
          <div className="container">
            <SearchBar />
          </div>
        )}
      </Wrapper>
    );
  }
}

const mapStateToProps = ({ results, facets, ui: { lang } }) => ({
  results: results,
  documents: results.documents,
  counts: facets.counts,
  totalDocumentCount: results.pagination.totalDocumentCount,
  lang
});

export default connect(
  mapStateToProps,
  {
    makeSearch
  }
)(Result);



// WEBPACK FOOTER //
// ./src/pages/Result/index.js